// Boon category taxonomy (frozen spec: .agent/tasks/boon-system-v1/spec.md).
// Every boon belongs to exactly one category — what it affects — derived in
// CODE from the upgrade's `stat.kind` (never the display name), so new
// content inherits its category automatically. Rarity stays the card frame
// color; the category adds an icon + accent chip on the card. Pure module:
// no DOM, so it is unit-testable under plain vitest.

export type BoonCategory = 'weapon' | 'turret' | 'hull' | 'engine' | 'systems';

export interface BoonCategoryMeta {
  /** HackerNoon pixel iconfont glyph (rendered as `hn hn-<icon>`). */
  icon: string;
  /** Accent color for the category chip. */
  color: string;
  /** Short uppercase label shown on the chip and in the legend. */
  label: string;
}

/** Category accents per the spec icon-code table. */
export const BOON_CATEGORY_META: Record<BoonCategory, BoonCategoryMeta> = {
  weapon: { icon: 'fire-solid', color: '#ffd36b', label: 'WEAPON' },
  turret: { icon: 'robot-solid', color: '#ffe95e', label: 'TURRET' },
  hull: { icon: 'octagon-check-solid', color: '#4db8ff', label: 'HULL' },
  engine: { icon: 'bolt', color: '#35e0ff', label: 'ENGINE' },
  systems: { icon: 'cog', color: '#3dffc4', label: 'SYSTEMS' },
};

/** Explicit stat.kind → category mapping from the spec table. */
const KIND_TO_CATEGORY: Record<string, BoonCategory> = {
  // WEAPON — auto-weapon offense.
  weapon_level: 'weapon',
  damage_mult: 'weapon',
  fire_rate_mult: 'weapon',
  projectile_count: 'weapon',
  projectile_pierce: 'weapon',
  bullet_split: 'weapon',
  crit_chance: 'weapon',
  weapon_scattergun: 'weapon',
  weapon_seeker: 'weapon',
  weapon_waveform: 'weapon',
  weapon_homing: 'weapon',
  weapon_burn: 'weapon',
  weapon_chill: 'weapon',
  weapon_chain: 'weapon',
  moving_fire_rate: 'weapon',
  // TURRET — manual laser / hold-ground.
  laser_damage_mult: 'turret',
  laser_rate_mult: 'turret',
  laser_pierce: 'turret',
  // HULL — defense & survival.
  max_hp: 'hull',
  shield: 'hull',
  shield_regen: 'hull',
  hull: 'hull',
  hull_damage_link: 'hull',
  revive: 'hull',
  // ENGINE — mobility.
  move_speed_mult: 'engine',
  dash_cooldown: 'engine',
  dash_charges: 'engine',
  dash_wake: 'engine',
  dash_haste: 'engine',
  dodge: 'engine',
  // SYSTEMS — utility & ship systems.
  magnet_radius_mult: 'systems',
  weapon_slots: 'systems',
  serenity_damage: 'systems',
};

/**
 * Map an upgrade to its category from `stat.kind` alone. The spec's wildcard
 * families (turret_*, mercy_*, serenity_*, zone_*) resolve by prefix; any
 * other unknown kind falls back to 'systems' so newly added content never
 * renders broken — it just shows the cog chip until classified.
 */
export function categorizeUpgrade(def: { stat: { kind: string } }): BoonCategory {
  const kind = def.stat.kind;
  const direct = KIND_TO_CATEGORY[kind];
  if (direct) return direct;
  if (kind.startsWith('turret_')) return 'turret';
  if (kind.startsWith('mercy_')) return 'hull';
  if (kind.startsWith('serenity_') || kind.startsWith('zone_')) return 'systems';
  return 'systems';
}
